import React, { forwardRef, useCallback, useEffect, useMemo, useState, type Ref } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import type { MenuId } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/types.tsx';
import { useSidebarNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { useSidebarNav4Controller } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-controller-context/index.tsx';
import { ExpandableMenuItem, type ExpandableMenuItemProps } from '@atlassian/navigation-system';
import { memoWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/memo-with-condition/index.tsx';

type Props = Omit<ExpandableMenuItemProps, 'isDefaultExpanded'> & {
	menuId: MenuId;
	onSystemToggle?: (isExpanded: boolean) => void;
	// Used for setting the ref specifically for the expanded or collapsed state
	// For example - triggering entrypoint load/unload on hovering over the collapsed menu item
	expandedRef?: Ref<HTMLDivElement>;
	collapsedRef?: Ref<HTMLDivElement>;
};

/**
 * Nav4ExpandableMenuItem provides `menuId` instead of `isDefaultExpanded`
 * `menuId` is used to set the expanded-state of the underlying ExpandableMenuItem, using the useSidebarNav4() hook.
 *
 * Ideally, we want to pass the menuId to the trigger element.
 */
export const Nav4ExpandableMenuItem = memoWithCondition(
	() => fg('jfp-magma-stability-check-nav4-batch-1'),
	forwardRef<HTMLDivElement, Props>(Nav4ExpandableMenuItemView),
);

function Nav4ExpandableMenuItemView(props: Props, ref: Ref<HTMLDivElement>) {
	// Suppressed due to new lint violation when upgrading to eslint-plugin-react-hooks v5

	const { isSelectedPath, isInitialSelectedPath } = useSidebarNav4();
	// Suppressed due to new lint violation when upgrading to eslint-plugin-react-hooks v5

	const { isControlledNav, isControlledPath } = useSidebarNav4Controller();
	const {
		children,
		menuId,
		onExpansionToggle,
		isExpanded = false,
		onSystemToggle,
		expandedRef,
		collapsedRef,
	} = props;
	// controlled state, `isActuallyExpanded` decides whether the component is expanded or not (based on logic in this component and props.isExpanded)
	// Suppressed due to new lint violation when upgrading to eslint-plugin-react-hooks v5

	const [isActuallyExpanded, setActuallyExpanded] = useState<boolean>(
		isInitialSelectedPath(menuId) || isExpanded,
	);

	// By default, the expandable component is closed. When we want it to be initially open, we also need to trigger onExpansionToggle().
	// Suppressed due to new lint violation when upgrading to eslint-plugin-react-hooks v5

	useEffect(() => {
		if (isActuallyExpanded === true && isExpanded === false) {
			onSystemToggle?.(isActuallyExpanded);
		}
		// We want to run this only once, on init.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// If isSelectedPath changes (external change), update our internal state.
	// Suppressed due to new lint violation when upgrading to eslint-plugin-react-hooks v5

	useEffect(() => {
		// Fyi, the following logic causes the component to _collapse_ when the selectedPath changes (preserved here for posterity):
		//   const localIsExpanded = isSelectedPath(menuId);

		// ...but this logic _keeps the component expanded_ (if it is) when the selectedPath changes. We can only auto-expand component, not auto-collapse it though (which is fine, for current UX):
		const localIsExpanded = isSelectedPath(menuId) || isActuallyExpanded;

		// Trigger the callbacks ONLY when the previous state doesn't match the new state.
		if (localIsExpanded !== isActuallyExpanded) {
			setActuallyExpanded(localIsExpanded);
			onSystemToggle?.(localIsExpanded);
		}

		// We only want to run this when isSelectedPath() changes, not when isActuallyExpanded changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSelectedPath]);

	// Suppressed due to new lint violation when upgrading to eslint-plugin-react-hooks v5

	useEffect(() => {
		if (!isControlledNav) {
			return;
		}
		const localIsExpanded = isControlledPath(menuId);

		if (localIsExpanded !== isActuallyExpanded) {
			setActuallyExpanded(localIsExpanded);
			onSystemToggle?.(localIsExpanded);
		}
		// We only want to run this when isControlledPath() changes, not when isActuallyExpanded changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isControlledPath]);

	// Suppressed due to new lint violation when upgrading to eslint-plugin-react-hooks v5

	const expandableMenuItemRef = useMemo(() => {
		if (isActuallyExpanded) {
			return expandedRef ?? ref;
		}
		return collapsedRef ?? ref;
	}, [collapsedRef, expandedRef, isActuallyExpanded, ref]);

	const onExpandableMenuItemToggle = useCallback(
		(hasExpanded: boolean) => {
			// Since we are controlling `isExpanded` state in this component (via `isActuallyExpanded`), we need to change it on onExpansionToggle user-event too.
			setActuallyExpanded(hasExpanded);
			onExpansionToggle?.(hasExpanded);
		},
		[onExpansionToggle],
	);

	return (
		<ExpandableMenuItem
			ref={expandableMenuItemRef}
			isExpanded={isActuallyExpanded}
			onExpansionToggle={
				fg('jfp-magma-stability-check-nav4-batch-1')
					? onExpandableMenuItemToggle
					: (hasExpanded: boolean) => {
							// Since we are controlling `isExpanded` state in this component (via `isActuallyExpanded`), we need to change it on onExpansionToggle user-event too.
							setActuallyExpanded(hasExpanded);
							onExpansionToggle?.(hasExpanded);
						}
			}
		>
			{children}
		</ExpandableMenuItem>
	);
}
